import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    image = "image",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    subImage = "subImage",
}

interface Options {
    id: string;
    name: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.subImage]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
    };
    extendedStyles?: CENBannerStyles;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: 1,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CENBannerStyles {
    CENbannerButton?: ButtonPresentationProps;
    CENbannerCenterWrapper?: InjectableCss;
    CENbannerHeading?: InjectableCss;
    CENbannerSubheading?: InjectableCss;
    container?: InjectableCss;
    image?: LazyImageProps;
    CENbannerOverlayWrapper?: InjectableCss;
    categoryDropdown?: InjectableCss;
    CENcategorySection?: InjectableCss;
    CENbannerButtonWrapper?: InjectableCss;
}

export const CENbannerStyles: CENBannerStyles = {
    container: {
        css: css`
            position: relative;
            margin-bottom: 104px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 78px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    CENbannerCenterWrapper: {
        css: css`
            position: absolute;
            top: 210px;
            -webkit-transform: translateY(100%);
            transform: translateY(100%);
            left: 80px;
            width: 52%;
            margin-top: 40px;
            @media (min-width: 1500px) {
                top: 250px;
            }
            @media (min-width: 1230px) and (max-width: 1299px) {
                top: 195px;
            }
            @media (min-width: 1160px) and (max-width: 1229px) {
                top: 165px;
            }
            @media (min-width: 1040px) and (max-width: 1159px) {
                top: 28%;
            }
            @media (min-width: 992px) and (max-width: 1039px) {
                top: 25%;
            }
            @media (min-width: 992px) and (max-width: 1299px) {
                width: 70%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        position: unset;
                        width: 100%;
                        padding: 0;
                        transform: unset;
                        margin: 0;
                    `,
                    css`
                        display: block;
                        position: unset;
                        width: 100%;
                        padding: 0;
                        transform: unset;
                        margin: 0;
                    `,
                    css`
                        display: block;
                        position: unset;
                        width: 100%;
                        padding: 0;
                        transform: unset;
                        margin: 0;
                    `,
                    null,
                ]);
            }}
        `,
    },
    CENbannerHeading: {
        css: css`
            color: ${COLORS.white};
            @media (min-width: 992px) and (max-width: 1159px) {
                font-size: 40px !important;
                line-height: 50px !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        color: ${getProp("theme.typography.h1.color")};
                        font-weight: 700;
                        font-size: 32px;
                        line-height: 40px;
                    `,
                    css`
                        color: ${getProp("theme.typography.h1.color")};
                        font-weight: 700;
                        font-size: 32px;
                        line-height: 40px;
                    `,
                    css`
                        color: ${getProp("theme.typography.h1.color")};
                        font-weight: 700;
                        font-size: 32px;
                        line-height: 40px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    CENbannerSubheading: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            margin-bottom: 40px !important;
            color: ${COLORS.white};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px !important;
                        font-size: 16px;
                    `,
                    css`
                        margin-bottom: 16px !important;
                        font-size: 16px;
                    `,
                    css`
                        margin-bottom: 16px !important;
                        font-size: 16px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    CENbannerButton: {
        mergeCss: true,
        variant: "tertiary",
        css: css`
            border-radius: 24px;
            padding: 12px 24px;
            height: 48px;
            margin-left: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 24px !important;
                        margin-left: 0;
                    `,
                    css`
                        margin-top: 24px !important;
                        margin-left: 0;
                    `,
                    css`
                        margin-top: 24px !important;
                        margin-left: 0;
                    `,
                    null,
                ]);
            }}
            span {
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
            }
        `,
    },
    image: {
        css: css`
            display: block;
            @media (min-width: 992px) {
                box-shadow: 0px 13px 16px rgba(38, 37, 37, 0.24);
            }
            img {
                width: 100%;
                display: flex;
                background-position: center center;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            margin-top: -4%;
                        `,
                        css`
                            margin-top: -4%;
                        `,
                        css`
                            margin-top: -4%;
                        `,
                        null,
                    ]);
                }}
            }
        `,
    },
    CENbannerOverlayWrapper: {
        css: css`
            position: absolute;
            top: 130px;
            left: 80px;
            width: 52%;
            z-index: 9;
            @media (min-width: 992px) {
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 1500px) {
                width: 40%;
                top: 170px;
            }
            @media (min-width: 1160px) and (max-width: 1280px) {
                width: 52%;
            }
            @media (min-width: 1230px) and (max-width: 1299px) {
                top: 95px;
            }
            @media (min-width: 1160px) and (max-width: 1229px) {
                top: 85px;
            }
            @media (min-width: 1040px) and (max-width: 1159px) {
                top: 19%;
            }
            @media (min-width: 992px) and (max-width: 1039px) {
                top: 16%;
            }
            @media (min-width: 992px) and (max-width: 1159px) {
                width: 44%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        position: unset;
                        padding: 32px 16px;
                        width: 100% !important;
                        background: #f2f2f2;
                    `,
                    css`
                        position: unset;
                        padding: 32px 16px;
                        width: 100% !important;
                        background: #f2f2f2;
                    `,
                    css`
                        position: unset;
                        padding: 32px 16px;
                        width: 100% !important;
                        background: #f2f2f2;
                    `,
                    null,
                ]);
            }}
        `,
    },
    CENcategorySection: {
        css: css`
            display: flex;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                ]);
            }}
            > div {
                flex: 1 1 0%;
            }
            span {
                &[data-test-selector="CENBanner-selectedText"] {
                    padding: 12px 24px !important;
                    padding-right: 56px !important;
                }

                &[class^="IconWrapper"] {
                    background: url("/userFiles/images/chevronDown.svg") no-repeat;
                    height: 42px !important;
                    background-position: center;
                    svg {
                        display: none;
                    }
                }
            }
            input {
                border: 2px solid #f2f2f2 !important;
                border-radius: 40px !important;
                padding: 12px 24px !important;
                padding-right: 56px !important;
                height: 48px !important;
            }
        `,
    },
    CENbannerButtonWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin: 0 auto;
                    `,
                    css`
                        margin: 0 auto;
                    `,
                    css`
                        margin: 0 auto;
                    `,
                    null,
                ]);
            }}
        `,
    },
};

export const CENHomepageBanner: FC<Props> = ({
    fields,
    extendedStyles,
    categoriesDataView,
    getCategoriesParameter,
    loadCategories,
    topLevelCategoryIds,
}) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    let categories;
    const [categoriesData, setCategoriesData] = useState<any>();
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedOption, setSelectedOption] = useState<undefined | string>("");
    const [selectedOpt, setSelectedOpt] = useState<undefined | string>("");

    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }, []);

    useEffect(() => {
        if (!!categoriesDataView.value && topLevelCategoryIds && !categoriesDataView.isLoading) {
            const cat =
                categoriesDataView.value !== undefined
                    ? categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1)
                    : [];
            cat.forEach(c => options.push({ optionText: c.name, optionValue: c.id }));
        }
    }, [categoriesDataView.isLoading]);

    const onClick = () => {
        if (selectedOption) {
            history.push(selectedOption);
        }
    };

    const backgroundStyles = `background-image: url(${fields.subImage})`;

    const styles = useMergeStyles("banner", CENbannerStyles, extendedStyles);

    const wrapperStyles = {
        css: css`
            width: 100%;
            display: flex;
            background-position: center center;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        ${backgroundStyles};
                        padding: 32px 16px;
                    `,
                    css`
                        ${backgroundStyles};
                        padding: 32px 16px;
                    `,
                    css`
                        ${backgroundStyles};
                        padding: 32px 16px;
                    `,
                    null,
                ]);
            }}
        `,
    };

    const handleOptionChange = (event: any) => {
        setSelectedOpt(event.toString());
        if (categoriesDataView.value) {
            const cat = categoriesDataView?.value.filter(o => o.id === event.toString());
            setSelectedOption(cat[0].path);
        }
    };
    return (
        <StyledWrapper className="CENbannerMainCotainer" {...styles.container}>
            <StyledWrapper className="CENbannerOverlayWrapper" {...styles.CENbannerOverlayWrapper}>
                <Typography variant="h1" className="CENbannerHeading" {...styles.CENbannerHeading}>
                    {fields.heading}
                </Typography>
            </StyledWrapper>
            <LazyImage className="CENImg" {...styles.image} src={fields.image} altText={fields.heading} />
            <StyledWrapper className="CENbannerCenterWrapper" {...styles.CENbannerCenterWrapper}>
                <StyledWrapper className="CENbannerWrapper" {...wrapperStyles}>
                    <Typography variant="p" className="CENbannerSubheading" {...styles.CENbannerSubheading}>
                        {fields.subheading}
                    </Typography>
                    <StyledWrapper {...styles.CENcategorySection}>
                        {!categoriesDataView.isLoading ? (
                            <DynamicDropdown
                                onSelectionChange={handleOptionChange}
                                options={options}
                                placeholder={translate("Pick the product category")}
                                data-test-selector="CENBanner"
                                selected={selectedOpt}
                                isLoading={categoriesDataView.isLoading}
                                {...styles.categoryDropdown}
                            />
                        ) : null}
                        <StyledWrapper {...styles.CENbannerButtonWrapper}>
                            <Button {...styles.CENbannerButton} onClick={onClick}>
                                {fields.buttonLabel}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const CENbanner: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENHomepageBanner),
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Banner Image",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "TextField",
                displayName: "Subheading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                name: fields.subImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Mobile Sub Heading Image",
            },
        ],
    },
};

export default CENbanner;
